import React, { useEffect, useState } from 'react';
import { Asset as ContentfulAsset } from 'contentful';

import { IStaticFullWidthHeroFields } from '@/types/contentful';

import { getImageData } from '../../utils/contentful';
import { useVideo } from '~/hooks/useVideo';

import { Box } from '../Box';
import { Column } from '../Layout';
import CtaWidget from '../CtaWidget';

import {
  CustomBlockContainer,
  AdditionalContentWrapper,
  ParagraphWithLineBreaks,
  StyledIcon
} from './styles';
import PrivacyPolicyDisclaimer from 'src/components/PrivacyPolicyDisclaimer';
import { Flexbox } from '../Flexbox';
import GlImage from '../Image';
import AdditionalContent from '../AdditionalContent';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import RichText from '../RichText';

import Lottie from 'lottie-react';
import { animated, useSpring, easings } from 'react-spring';
import { usePageDataContext } from '~/context/PageData';
import cx from 'classnames';
import { SmartText } from '../SmartText';
import { Asset } from '../AtomicComponents/Asset';
import { captureError, trackRumEvent } from '../../utils/datadogRum';
import { MediaControlButton } from '../AtomicComponents/MediaControlButton';

function useAnimatedFadeIn(key: number) {
  return useSpring({
    to: { opacity: 1, transform: 'translateY(0)' },
    from: { opacity: 0, transform: 'translateY(50px)' },
    delay: 300 + key * 500,
    config: {
      duration: 1500,
      easing: easings.easeOutExpo
    }
  });
}

export default function AnimatedFullWidthHero({
  headingText,
  subheadingText,
  desktopBackgroundImage,
  fullwidthDesktopImage,
  animatedImage,
  mobileImage,
  additionalText,
  privacyPolicyDisclaimer,
  enableBlackText,
  ctaWidget,
  additionalContent,
  returningUserHeadingText,
  customTextColor,
  backgroundColor,
  eyebrowImage,
  assetFirstOnMobile,
  enablePaddingOnMobileImage
}: IStaticFullWidthHeroFields) {
  const { isReturning } = usePageDataContext();
  const visibility =
    fullwidthDesktopImage || mobileImage ? 'visible' : 'hidden';

  // Check if assets are videos (based on content type)
  const isDesktopVideo =
    fullwidthDesktopImage?.fields?.file?.contentType?.includes('mp4') ||
    fullwidthDesktopImage?.fields?.file?.contentType?.includes('video');
  const isMobileVideo =
    mobileImage?.fields?.file?.contentType?.includes('mp4') ||
    mobileImage?.fields?.file?.contentType?.includes('video');

  const isDesktopImage =
    fullwidthDesktopImage?.fields?.file?.contentType?.includes('image');
  const isMobileImage =
    mobileImage?.fields?.file?.contentType?.includes('image');

  const desktopRawUrl = isDesktopVideo
    ? fullwidthDesktopImage?.fields?.file?.url || ''
    : '';
  const mobileRawUrl = isMobileVideo
    ? mobileImage?.fields?.file?.url || ''
    : '';

  const desktopPosterUrl =
    isDesktopVideo && isMobileImage
      ? mobileImage?.fields?.file?.url
      : undefined;
  const mobilePosterUrl =
    isMobileVideo && isDesktopImage
      ? fullwidthDesktopImage?.fields?.file?.url
      : undefined;

  const desktopUrl = desktopRawUrl;
  const mobileUrl = mobileRawUrl;

  const { videoRef: desktopVideoRef, sourceUrl: desktopSourceUrl } = useVideo(
    desktopUrl,
    { manageVisibility: false, deferUntilVisible: true }
  );
  const { videoRef: mobileVideoRef, sourceUrl: mobileSourceUrl } = useVideo(
    mobileUrl,
    { manageVisibility: false, deferUntilVisible: true }
  );

  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnded, setIsEnded] = useState(false);

  useEffect(() => {
    const videos: HTMLVideoElement[] = [
      (desktopVideoRef as React.MutableRefObject<HTMLVideoElement | null>)
        .current,
      (mobileVideoRef as React.MutableRefObject<HTMLVideoElement | null>)
        .current
    ].filter(Boolean) as HTMLVideoElement[];
    if (!videos.length) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches) {
      videos.forEach((v) => v.pause());
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const v = e.target as HTMLVideoElement;
          if (e.isIntersecting) {
            v.play()
              .then(() => {
                setIsPlaying(true);
                setIsEnded(false);
                setAutoplayBlocked(false);
              })
              .catch((err: DOMException) => {
                if (err.name === 'NotAllowedError') {
                  trackRumEvent('autoplay_blocked', {
                    page_url: window.location.href,
                    component: 'AnimatedFullWidthHero'
                  });
                  captureError(err);
                  setAutoplayBlocked(true);
                }
              });
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    videos.forEach((v) => io.observe(v));
    const onPlay = () => {
      setIsPlaying(true);
      setIsEnded(false);
    };
    const onPause = () => {
      setIsPlaying(false);
    };
    const onEnded = () => {
      setIsEnded(true);
      setIsPlaying(false);
    };
    videos.forEach((v) => {
      v.addEventListener('play', onPlay);
      v.addEventListener('pause', onPause);
      v.addEventListener('ended', onEnded);
    });
    return () => {
      io.disconnect();
      videos.forEach((v) => {
        v.removeEventListener('play', onPlay);
        v.removeEventListener('pause', onPause);
        v.removeEventListener('ended', onEnded);
      });
    };
  }, [desktopUrl, mobileUrl]);

  // Determines the whether text color should be overridden
  const defaultTextColor = enableBlackText ? 'black' : 'white';
  const textColorOverride = customTextColor
    ? customTextColor
    : defaultTextColor;

  //Determines the hero media to display
  const determineHeroMedia = (
    assetFirstOnMobileEnabled: boolean,
    bottomMobileAsset: boolean
  ) => {
    if (animatedImage) {
      return (
        <Lottie
          data-testid="animated-image"
          className={cx('mobile:h-[408px] tablet:h-[460px] lg:h-[560px]', {
            hidden: assetFirstOnMobileEnabled && bottomMobileAsset
          })}
          animationData={animatedImage}
          loop={true}
        />
      );
    }

    if (isDesktopVideo && isMobileVideo) {
      return (
        <>
          <div className="hidden tablet:block tablet:pl-4 min-[1000px]:pl-0">
            <video
              ref={desktopVideoRef}
              data-testid="desktop-video"
              className="h-full max-h-[584px] w-full object-contain object-center"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              crossOrigin="anonymous"
              src={desktopSourceUrl}
              poster={desktopPosterUrl}
            />
          </div>
          <div className="block tablet:hidden">
            <video
              ref={mobileVideoRef}
              data-testid="mobile-video"
              className={cx('h-full w-full object-cover object-center', {
                hidden: assetFirstOnMobileEnabled && bottomMobileAsset,
                'p-6': !!enablePaddingOnMobileImage
              })}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              crossOrigin="anonymous"
              src={mobileSourceUrl}
              poster={mobilePosterUrl}
            />
          </div>
        </>
      );
    } else {
      // If there is no video, render the GL images
      return (
        <>
          <div className="hidden tablet:block">
            <Asset
              asset={fullwidthDesktopImage as ContentfulAsset}
              loading="eager"
              className={cx('h-full w-full [&>img]:object-contain', {
                hidden: visibility === 'hidden'
              })}
            />
          </div>
          <div
            data-testid="mobile-image"
            className={cx('block pt-3 tablet:hidden', {
              hidden: assetFirstOnMobileEnabled && bottomMobileAsset,
              'p-6': !!enablePaddingOnMobileImage
            })}
          >
            <Asset
              asset={mobileImage as ContentfulAsset}
              data-testid="fullwidth-hero-mobile-background"
              loading="eager"
              className={cx('h-full w-full [&>img]:object-contain', {
                hidden: visibility === 'hidden'
              })}
            />
          </div>
        </>
      );
    }
  };

  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node, children) => {
      return (
        <ParagraphWithLineBreaks
          as="p"
          fontSize={{
            0: '2xs',
            tablet: 's'
          }}
          fontWeight="400"
          colour={textColorOverride}
          textAlign="left"
        >
          {children}
        </ParagraphWithLineBreaks>
      );
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => (
      <StyledIcon {...getImageData(node.data.target)} />
    )
  };

  return (
    <Box
      data-blockid="static-fullwidth-hero"
      data-e2e="hero"
      data-testid="static-fullwidth-hero"
      className=""
    >
      <div
        className={'md:max-h-[624px] lg:h/full relative grid w-full pb-8'}
        style={{
          backgroundColor: backgroundColor ? backgroundColor : 'transparent'
        }}
      >
        {desktopBackgroundImage && (
          <div style={{ gridArea: '1/1' }}>
            <GlImage
              {...getImageData(desktopBackgroundImage as ContentfulAsset)}
              loading="eager"
              heroImage={false}
              layout="fill"
              objectFit="fill"
              width={undefined}
              height={undefined}
            />
          </div>
        )}
        <div
          style={{
            gridArea: '1/1',
            position: 'relative',
            placeItems: 'center',
            display: 'grid'
          }}
        >
          <MediaControlButton
            visible={autoplayBlocked}
            isPlaying={isPlaying}
            isEnded={isEnded}
            position="bottom-right"
            onClick={async () => {
              const isDesktop = window.matchMedia(
                '(min-width: 1024px)'
              ).matches;
              const target =
                (isDesktop
                  ? (
                      desktopVideoRef as React.MutableRefObject<HTMLVideoElement | null>
                    ).current
                  : (
                      mobileVideoRef as React.MutableRefObject<HTMLVideoElement | null>
                    ).current) ||
                (
                  desktopVideoRef as React.MutableRefObject<HTMLVideoElement | null>
                ).current ||
                (
                  mobileVideoRef as React.MutableRefObject<HTMLVideoElement | null>
                ).current;
              if (!target) return;
              try {
                if (isPlaying) {
                  target.pause();
                } else {
                  if (isEnded) target.currentTime = 0;
                  await target.play();
                }
              } catch (err) {
                captureError(err as Error);
              }
            }}
          />
          {assetFirstOnMobile && (
            <animated.div className="relative left-0 top-0 block w-full tablet:hidden">
              {determineHeroMedia(!!assetFirstOnMobile, false)}
            </animated.div>
          )}
          <CustomBlockContainer
            marginTop={{
              0: 'ml',
              tablet: 'blockMargin'
            }}
            xPaddingOverrides={{ 0: 0, min: 0, mobile: 0 }}
            paddingBottom={
              !additionalContent
                ? {}
                : {
                    0: 'blockMarginY',
                    tablet: 'l'
                  }
            }
            revealOnScroll={false}
          >
            <div className="layout layout-padding">
              <div className="z-[1] order-2 mb-6 flex w-11/12 shrink-0 grow-0 flex-col items-center justify-center tablet:mb-8 tablet:w-full min-[1000px]:order-1 min-[1000px]:mb-0 min-[1000px]:w-6/12">
                <div className="mx-auto flex flex-col items-center justify-center min-[1000px]:items-start min-[1000px]:justify-start">
                  {eyebrowImage && (
                    <div className="z-1 mb-7 h-6 w-36">
                      <Asset
                        asset={eyebrowImage as ContentfulAsset}
                        loading="lazy"
                        className="h-6 w-36 object-contain"
                      />
                    </div>
                  )}
                  <SmartText>
                    <animated.div style={useAnimatedFadeIn(0)}>
                      <h1
                        data-testid="fullwidth-hero-headline"
                        className={`min:leading-[38.4px] mx-auto mb-4 mt-0 text-center text-[32px] font-normal tablet:mx-0 min-[1000px]:text-left min-[1000px]:text-[52px] min-[1000px]:leading-[55.2px]`}
                        style={{ color: textColorOverride }}
                      >
                        {isReturning
                          ? returningUserHeadingText ?? headingText
                          : headingText}
                      </h1>
                    </animated.div>
                    <animated.div style={useAnimatedFadeIn(1)}>
                      <p
                        data-testid="fullwidth-hero-subhead"
                        className={`mx-auto mb-6 mt-0 text-center text-xl tablet:mx-0 tablet:text-2xl min-[1000px]:text-left`}
                        style={{ color: textColorOverride }}
                      >
                        {subheadingText}
                      </p>
                    </animated.div>
                  </SmartText>
                  <animated.div style={useAnimatedFadeIn(2)}>
                    {ctaWidget && (
                      <div className="mb-2 flex justify-start tablet:max-w-[350px] min-[1000px]:mb-0 min-[1000px]:justify-normal [&_.role-select-container]:items-center [&_.role-select-container]:min-[1000px]:items-start [&_.role-select-label]:text-center">
                        <CtaWidget
                          {...ctaWidget?.fields}
                          sysId={ctaWidget?.sys?.contentType?.sys?.id}
                          id="fullwidth-hero-cta"
                          module="Hero"
                          width={'100%'}
                          isMobile={false}
                          responsive
                        />
                      </div>
                    )}
                    {additionalText && (
                      <Column size={10} margin={0}>
                        <Flexbox
                          flexDirection="row"
                          justifyContent="space-between"
                          margin={0}
                          alignItems="left"
                        >
                          <AdditionalContentWrapper data-e2e="additional-content-container">
                            <RichText
                              data={additionalText}
                              overrideOptions={RICH_TEXT_OVERRIDE}
                            />
                          </AdditionalContentWrapper>
                        </Flexbox>
                      </Column>
                    )}
                    {privacyPolicyDisclaimer && (
                      <div className="mt-2">
                        <PrivacyPolicyDisclaimer
                          privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                          displayPrivacyPolicyIcon={false}
                          color={defaultTextColor}
                        />
                      </div>
                    )}
                  </animated.div>
                </div>
              </div>
              <div className="tablet:px-auto order-1 mb-6 mt-0 flex w-full shrink-0 grow-0 flex-col items-center justify-center px-3 tablet:mb-8 min-[1000px]:order-2 min-[1000px]:mb-0 min-[1000px]:w-6/12">
                <animated.div style={useAnimatedFadeIn(3)}>
                  {determineHeroMedia(!!assetFirstOnMobile, true)}
                </animated.div>
              </div>
            </div>
          </CustomBlockContainer>
        </div>
      </div>
      <animated.div style={useAnimatedFadeIn(4)}>
        <AdditionalContent additionalContent={additionalContent} />
      </animated.div>
    </Box>
  );
}
